'use client';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import _ from '@dr-pam/common-types/json';
import React, { ReactNode, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'react-loading-skeleton/dist/skeleton.css';
import DateTimeUtils, { DATE_HUMAN_FORMAT } from '../../Utils/DateTimeUtils';
import type { ArticleJsonLd } from '../../Services/Server/ArticleService';

export type ArticleHtmlProps = {
	title: ReactNode;
	html?: string;
	frontmatter?: PrismaJson.ContentFrontmatter;
	jsonLd?: ArticleJsonLd;
	children?: ReactNode;
	skeleton?: boolean;
	showMetadata?: boolean;
};

export default function ArticleHtml(props: ArticleHtmlProps) {
	const { title, html, frontmatter, jsonLd, children, skeleton } = props;
	const showMetadata = props.showMetadata ?? true;

	const [markdownEl, setMarkdownEl] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!markdownEl) {
			return;
		}

		markdownEl.querySelectorAll('table').forEach((table) => {
			const parent = table.parentElement;
			if (!parent || parent.classList.contains('table-wrapper')) {
				return;
			}
			// wrap the table in a div
			const wrapper = document.createElement('div');
			wrapper.className = 'table-wrapper';
			parent.replaceChild(wrapper, table);
			wrapper.appendChild(table);
		});

		tippy('[data-tippy-content]').forEach((tip) => {
			tip.reference.addEventListener('click', (event: Event) => {
				event.preventDefault();
			});
		});
	}, [markdownEl, frontmatter]);

	const author = `${frontmatter?.author ?? jsonLd?.author.name ?? 'Dr Pamela Douglas'}`;
	const publishedDate = jsonLd ? DateTimeUtils.parse(jsonLd.datePublished) : undefined;
	const updatedDate = jsonLd ? DateTimeUtils.parse(jsonLd.dateModified) : undefined;

	const skeletonCount = html ? Math.ceil(DateTimeUtils.calculateReadTime(html).words / 20) : 5;

	return (
		<>
			{jsonLd ? (
				<script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
			) : null}
			<article className="ArticleHtml article paid-content">
				{title}
				{showMetadata && (
					<div className="meta">
						<span className="author">{author}</span>
						{publishedDate && (
							<span className="published-date">
								{DateTimeUtils.format(publishedDate, DATE_HUMAN_FORMAT)}
							</span>
						)}
						{updatedDate && (
							<span className="last-updated-date">
								{DateTimeUtils.format(updatedDate, DATE_HUMAN_FORMAT)}
							</span>
						)}
					</div>
				)}
				{skeleton ? (
					<Skeleton
						count={skeletonCount}
						height={30}
						enableAnimation={false}
						baseColor="#cad9e2"
						style={{ marginBottom: '30px' }}
					/>
				) : html ? (
					<div ref={setMarkdownEl} className="markdown" dangerouslySetInnerHTML={{ __html: html }} />
				) : (
					children
				)}
			</article>
		</>
	);
}
